import { TFunction } from 'i18next';
import i18n from 'i18n';
import {
  formatCsvFilenameDatestamp,
  formatLocalizedWeekday,
} from 'Utils/formatters/date';

export const GUEST_CSV_COLUMNS_TO_OMIT = ['dateTime'] as const;
export const GUEST_CSV_HEADER_KEY_PREFIX = 'guestZone.download.csvHeaders';
const GUEST_CSV_KNOWN_VISITOR_TYPES = ['New', 'Repeat'] as const;

export type GuestCsvHeader = { label: string; key: string };

export const parseCsv = (csv: string): string[][] => {
  const rows: string[][] = [];
  let row: string[] = [];
  let field = '';
  let inQuotes = false;
  const text = csv.replace(/^\uFEFF/, '');

  for (let i = 0; i < text.length; i++) {
    const char = text[i];
    const next = text[i + 1];

    if (inQuotes) {
      if (char === '"' && next === '"') {
        field += '"';
        i += 1;
      } else if (char === '"') {
        inQuotes = false;
      } else {
        field += char;
      }
    } else if (char === '"') {
      inQuotes = true;
    } else if (char === ',') {
      row.push(field);
      field = '';
    } else if (char === '\n' || char === '\r') {
      row.push(field);
      field = '';
      rows.push(row);
      row = [];

      if (char === '\r' && next === '\n') {
        i += 1;
      }
    } else {
      field += char;
    }
  }

  if (field.length > 0 || row.length > 0) {
    row.push(field);
    rows.push(row);
  }

  return rows.filter((currentRow) => currentRow.some((value) => value !== ''));
};

const serializeCsvField = (value: string): string => {
  const safeValue = /^[\t\r\n ]*[=+\-@]/.test(value) ? `'${value}` : value;

  if (/[",\r\n]/.test(safeValue)) {
    return `"${safeValue.replace(/"/g, '""')}"`;
  }

  return safeValue;
};

export const serializeCsv = (rows: string[][]): string =>
  rows.map((row) => row.map(serializeCsvField).join(',')).join('\n');

export const getGuestCsvHeaderLabel = (
  apiProperty: string,
  t: TFunction = i18n.t
): string =>
  t(`${GUEST_CSV_HEADER_KEY_PREFIX}.${apiProperty}`, {
    defaultValue: apiProperty,
  });

export const getGuestCsvHeaders = (
  apiProperties: string[],
  t: TFunction = i18n.t
): GuestCsvHeader[] =>
  apiProperties.map((key) => ({
    key,
    label: getGuestCsvHeaderLabel(key, t),
  }));

export const removeCsvColumns = (
  csv: string,
  columnsToRemove: readonly string[]
): string => {
  if (!csv.trim()) {
    return csv;
  }

  const rows = parseCsv(csv);

  if (!rows.length) {
    return csv;
  }

  const indexesToDrop = new Set(
    columnsToRemove
      .map((column) => rows[0].indexOf(column))
      .filter((index) => index >= 0)
  );

  if (!indexesToDrop.size) {
    return csv;
  }

  return serializeCsv(
    rows.map((row) => row.filter((_, index) => !indexesToDrop.has(index)))
  );
};

export const prepareGuestCsvForDownload = (
  csv: string,
  t: TFunction = i18n.t
): string => {
  if (!csv.trim()) {
    return csv;
  }

  const rows = parseCsv(csv);

  if (!rows.length) {
    return csv;
  }

  const indexesToDrop = new Set(
    GUEST_CSV_COLUMNS_TO_OMIT.map((column) => rows[0].indexOf(column)).filter(
      (index) => index >= 0
    )
  );

  const filteredRows = indexesToDrop.size
    ? rows.map((row) => row.filter((_, index) => !indexesToDrop.has(index)))
    : rows;

  const apiHeadings = filteredRows[0];

  return serializeCsv(
    filteredRows.map((row, rowIndex) =>
      rowIndex === 0
        ? getGuestCsvHeaders(row, t).map((header) => header.label)
        : localizeGuestCsvRow(apiHeadings, row, t)
    )
  );
};

export const localizeGuestCsvVisitorType = (
  value: string,
  t: TFunction = i18n.t
): string => {
  if (!value) {
    return value;
  }

  if (
    GUEST_CSV_KNOWN_VISITOR_TYPES.includes(
      value as (typeof GUEST_CSV_KNOWN_VISITOR_TYPES)[number]
    )
  ) {
    return t(`guestZone.download.visitorTypes.${value}`);
  }

  return t('guestZone.download.visitorTypes.unknown', { value });
};

export const getGuestCsvFilename = (
  timezone: string,
  t: TFunction = i18n.t
): string => {
  const name = t('guestZone.download.csvFilename', { defaultValue: 'guests' });
  const period = t('common.last30Days', { defaultValue: 'last 30 days' });
  const datestamp = formatCsvFilenameDatestamp(timezone);

  return `${name}-${period}-${datestamp}.csv`;
};

export const localizeGuestCsvRow = (
  apiHeadings: string[],
  row: string[],
  t: TFunction = i18n.t
): string[] => {
  const visitDate = row[apiHeadings.indexOf('visitDate')] ?? '';

  return row.map((value, index) => {
    const column = apiHeadings[index];

    if (column === 'visitorType') {
      return localizeGuestCsvVisitorType(value, t);
    }

    if (column === 'visitDayOfWeek') {
      return formatLocalizedWeekday(visitDate, value);
    }

    return value;
  });
};
